import { createSlice } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { handleDefaultErrors } from '../../resources/functions';

export interface QRScan {
  user?: {
    phone?: string;
    firstName?: string;
    lastName?: string;
  };
  lat?: number;
  lon?: number;
  createdAt: string;
}

export interface LostScooter {
  _id: string;
  name: string;
  status: string;
  coords?: {
    lat: number;
    lon: number;
  };
  updatedAt?: string;
}

export interface Scooter {
  _id: string;
  name: string;
  status: string;
  city?: {
    _id: string;
    name?: string;
  } | string;
}

export interface LostScootersState {
  items: LostScooter[];
  loading: boolean;
  qrScans: QRScan[];
  qrScansLoading: boolean;
  allScooters: Scooter[];
  scootersLoading: boolean;
}

const initialState: LostScootersState = {
  items: [],
  loading: false,
  qrScans: [],
  qrScansLoading: false,
  allScooters: [],
  scootersLoading: false
};

export const lostScootersSlice = createSlice({
  name: 'lostScooters',
  initialState,
  reducers: {
    setLostScooters: (state, action) => {
      state.items = action.payload;
      state.loading = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    addLostScooter: (state, action) => {
      state.items.unshift(action.payload);
    },
    removeLostScooter: (state, action) => {
      state.items = state.items.filter(item => item._id !== action.payload);
    },
    setQRScans: (state, action) => {
      state.qrScans = action.payload;
      state.qrScansLoading = false;
    },
    setQRScansLoading: (state, action) => {
      state.qrScansLoading = action.payload;
    },
    setAllScooters: (state, action) => {
      state.allScooters = action.payload;
      state.scootersLoading = false;
    },
    setScootersLoading: (state, action) => {
      state.scootersLoading = action.payload;
    }
  }
});

export const {
  setLostScooters,
  setLoading,
  addLostScooter,
  removeLostScooter,
  setQRScans,
  setQRScansLoading,
  setAllScooters,
  setScootersLoading
} = lostScootersSlice.actions;

export const selectLostScooters = (state: RootState) => state.lostScooters;

export const getLostScootersAsync = (): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    const response = await axios.get('/scooters/searching_coords');
    
    // Backend returns "result" (singular), not "results" (plural)
    const results = response.data?.data?.result || response.data?.data?.results || [];
    
    const allScans: QRScan[] = [];
    results.forEach((scooter: any) => {
      if (scooter.scans && Array.isArray(scooter.scans)) {
        scooter.scans.forEach((scan: QRScan) => {
          allScans.push({
            ...scan,
            scooterId: scooter._id
          } as any);
        });
      }
    });
    
    dispatch(setLostScooters(results));
    dispatch(setQRScans(allScans));
  } catch (e: any) {
    handleDefaultErrors(e);
    dispatch(setLoading(false));
  }
};

export const addLostScooterAsync =
  (scooterId: string, success?: () => void, error?: () => void): AppThunk =>
  async dispatch => {
    try {
      await axios.put(`/scooters/${scooterId}/info`, { isSearching: true });
      if (success) {
        success();
      }
    } catch (e: any) {
      handleDefaultErrors(e);
      if (error) {
        error();
      }
    }
  };

export const removeLostScooterAsync =
  (scooterId: string, success?: () => void): AppThunk =>
  async dispatch => {
    try {
      await axios.put(`/scooters/${scooterId}/info`, { isSearching: false });
      if (success) {
        success();
      }
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const getQRScansAsync =
  (scooterId: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(setQRScansLoading(true));
      const response = await axios.get('/scooters/searching_coords');
      const results = response.data?.data?.result || response.data?.data?.results || [];
      
      const scooter = results.find((s: any) => s._id === scooterId);
      const scans = scooter?.scans || [];
      
      dispatch(setQRScans(scans));
    } catch (e: any) {
      handleDefaultErrors(e);
      dispatch(setQRScansLoading(false));
    }
  };

export const getAllScootersAsync = (): AppThunk => async dispatch => {
  try {
    dispatch(setScootersLoading(true));
    const response = await axios.get('/scooters?version=2');
    // Backend may return scooters at different nesting levels
    const scooters = response.data?.data?.scooters || response.data?.scooters || [];
    dispatch(setAllScooters(scooters));
  } catch (e: any) {
    handleDefaultErrors(e);
    dispatch(setScootersLoading(false));
  }
};

export default lostScootersSlice.reducer;
